/**
 * TenantSwitcher Component
 *
 * Dropdown for switching between households when user belongs to multiple.
 * Only displayed when user has 2+ households.
 *
 * WP-TENANT-1: Tenant Switching Implementation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/utils/cn';
import { useMyHouseholds } from '@/hooks/useHouseholds';
import { useAuthStore } from '@/store/authStore';
import { setAccessToken } from '@/services/api';
import { switchTenant } from '@/services/auth';
import type { Household } from '@/types/household';

/**
 * Get initials from a household name
 */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  const firstWord = words[0];
  const secondWord = words[1];
  if (words.length >= 2 && firstWord && secondWord && firstWord[0] && secondWord[0]) {
    return (firstWord[0] + secondWord[0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export default function TenantSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const currentTenantId = user?.tenantId;

  const { data: householdsData, isLoading } = useMyHouseholds();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    },
    []
  );

  /**
   * Handle switching to a different tenant.
   *
   * NOTE: This hook MUST be defined before any early returns to comply with
   * React's Rules of Hooks. Hooks must be called in the same order on every render.
   * See: https://react.dev/reference/rules/rules-of-hooks
   *
   * WP-BUG-34: Fixed hooks violation that caused React error #310
   *
   * 1. Calls backend to get new tokens
   * 2. Updates auth context with new tokens
   * 3. Clears React Query cache (data is tenant-scoped)
   * 4. Redirects to dashboard
   */
  const handleSwitchTenant = useCallback(
    async (tenantId: string) => {
      // Don't switch to current tenant
      if (tenantId === currentTenantId) {
        setIsOpen(false);
        return;
      }

      setIsSwitching(true);

      try {
        // Call API to get new tokens
        const result = await switchTenant(tenantId);

        // WP-AUTH-12: Only update access token - refresh token is managed via httpOnly cookie
        setAccessToken(result.accessToken);

        // Update user context with new tenant info
        if (user) {
          setUser({
            ...user,
            tenantId: result.tenantId,
            role: result.role,
          });
        }

        // Clear ALL cached data - it's all tenant-scoped
        // This forces refetch with new tenant context
        queryClient.clear();

        // Show success message
        toast.success(`Switched to ${result.tenantName}`);

        // Close dropdown
        setIsOpen(false);

        // Navigate to dashboard (or refresh current page)
        navigate('/dashboard', { replace: true });
      } catch (error) {
        // Handle errors with appropriate messages
        if (error instanceof Error) {
          if (error.message.includes('403')) {
            toast.error('You are not a member of this household');
          } else if (error.message.includes('404')) {
            toast.error('Household not found');
          } else {
            toast.error('Failed to switch household. Please try again.');
          }
        } else {
          toast.error('Failed to switch household');
        }
      } finally {
        setIsSwitching(false);
      }
    },
    [currentTenantId, navigate, queryClient, setUser, user]
  );

  // Don't render if loading, no data, or only one household
  // NOTE: This early return is AFTER all hooks - this is correct per Rules of Hooks
  if (isLoading || !householdsData || householdsData.count <= 1) {
    return null;
  }

  // These variables depend on loaded data, so they come after the early return
  const households = householdsData.households;
  const currentHousehold = households.find((h: Household) => h.id === currentTenantId);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 rounded-lg',
          'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          'transition-colors'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Current household: ${currentHousehold?.name || 'Unknown'}. Click to switch.`}
      >
        {/* Household avatar */}
        <div className="w-6 h-6 rounded bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
          {currentHousehold ? getInitials(currentHousehold.name) : '?'}
        </div>

        {/* Household name (hidden on mobile) */}
        <span className="hidden sm:block max-w-[150px] truncate">
          {currentHousehold?.name || 'Unknown'}
        </span>

        {/* Chevron icon */}
        <svg
          className={cn('w-4 h-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 mt-2 w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
          role="listbox"
          aria-label="Select household"
          onKeyDown={handleKeyDown}
        >
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Your Households
            </div>

            {households.map((household: Household) => (
              <button
                key={household.id}
                onClick={() => handleSwitchTenant(household.id)}
                disabled={isSwitching || household.id === currentTenantId}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm',
                  'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                  household.id === currentTenantId && 'bg-primary-50',
                  isSwitching && 'opacity-50 cursor-not-allowed'
                )}
                role="option"
                aria-selected={household.id === currentTenantId}
              >
                {/* Household avatar */}
                <div
                  className={cn(
                    'w-8 h-8 rounded flex items-center justify-center text-xs font-semibold',
                    household.id === currentTenantId
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-700'
                  )}
                >
                  {getInitials(household.name)}
                </div>

                {/* Household info */}
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'font-medium',
                        household.id === currentTenantId ? 'text-primary-700' : 'text-gray-900'
                      )}
                    >
                      {household.name}
                    </span>
                    {household.role === 'owner' && (
                      <span className="text-xs text-gray-500">(Owner)</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {household.memberCount} member{household.memberCount !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Check icon for current household */}
                {household.id === currentTenantId && (
                  <svg
                    className="w-5 h-5 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading overlay during switch */}
      {isSwitching && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <svg
              className="animate-spin h-8 w-8 text-primary-600 mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">Switching household...</p>
          </div>
        </div>
      )}
    </div>
  );
}
